/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { RtlSide } from '../../UNSAFE_Floating';

/**
 * Returns the normalized Position depending on the reading direction
 * @param side The side that should be normalized
 * @param direction The reading direction
 * @returns The normalized 'right' or 'left' position if the side was 'start' or 'end', otherwise returns unchanged position
 */
export const normalizePosition = (side: RtlSide, direction: 'rtl' | 'ltr'): RtlSide => {
  const sideHashLtr = {
    start: 'left',
    end: 'right'
  };
  const sideHashRtl = {
    start: 'right',
    end: 'left'
  };
  if (direction === 'rtl') {
    return side.replace(
      /start|end/g,
      (matched) => sideHashRtl[matched as keyof typeof sideHashRtl]
    ) as RtlSide;
  } else {
    return side.replace(
      /start|end/g,
      (matched) => sideHashLtr[matched as keyof typeof sideHashLtr]
    ) as RtlSide;
  }
};
/**
 * Returns the reversed normalized Position depending on the reading direction
 * @param side The side that should be reversed
 * @param direction The reading direction
 * @returns The 'start' or 'end' position if the side was 'right' or 'left', otherwise returns unchanged position
 */
export const reverseNormalizePosition = (side: RtlSide, direction: 'rtl' | 'ltr'): RtlSide => {
  const sideHashLtr = {
    left: 'start',
    right: 'end'
  };
  const sideHashRtl = {
    right: 'start',
    left: 'end'
  };
  if (direction === 'rtl') {
    return side.replace(
      /left|right/g,
      (matched) => sideHashRtl[matched as keyof typeof sideHashRtl]
    ) as RtlSide;
  } else {
    return side.replace(
      /left|right/g,
      (matched) => sideHashLtr[matched as keyof typeof sideHashLtr]
    ) as RtlSide;
  }
};

/**
 * Check is 'window' is available
 */
const hasWindow = () => {
  return typeof window !== 'undefined';
};

/**
 * Returns the window object for the node
 */
const getWindow = (node: any): typeof window => {
  return node?.ownerDocument?.defaultView || window;
};

/**
 * Checks if the value is a valid node
 */
const isNode = (value: unknown): value is Node => {
  if (!hasWindow()) {
    return false;
  }
  return value instanceof Node || value instanceof getWindow(value).Node;
};

/**
 * Checks if the node is the last traversable ancestor
 */
const isLastTraversableNode = (node: Node): boolean => {
  return ['html', 'body', '#document'].includes(getNodeName(node));
};

/**
 * Returns document element for the node
 */
const getDocumentElement = (node: Node | Window): HTMLElement => {
  return ((isNode(node) ? node.ownerDocument : node.document) || window.document)?.documentElement;
};

/**
 * Returns normalized node name
 */
const getNodeName = (node: Node | Window): string => {
  if (isNode(node)) {
    return (node.nodeName || '').toLowerCase();
  }
  return '#document';
};

/**
 * Returns node's parent
 */
const getParentNode = (node: Node): Node => {
  if (getNodeName(node) === 'html') {
    return node;
  }
  return node.parentNode || getDocumentElement(node);
};

/**
 * Checks if the value is an HTMLElement
 */
const isHTMLElement = (value: unknown): value is HTMLElement => {
  if (!hasWindow()) {
    return false;
  }
  return value instanceof HTMLElement || value instanceof getWindow(value).HTMLElement;
};

/**
 * Checks if the element is a containing block
 */
const isContainingBlock = (element: HTMLElement) => {
  const css = getComputedStyle(element);

  return (
    css.transform !== 'none' ||
    css.perspective !== 'none' ||
    (css.containerType ? css.containerType !== 'normal' : false) ||
    ['transform', 'perspective', 'filter'].some((value) =>
      (css.willChange || '').includes(value)
    ) ||
    ['paint', 'layout', 'strict', 'content'].some((value) => (css.contain || '').includes(value))
  );
};

/**
 * Returns element's containg block or null (if no containing block exists)
 */
export const getContainingBlockElem = (element: HTMLElement) => {
  let currentNode: Node | null = getParentNode(element);

  while (isHTMLElement(currentNode) && !isLastTraversableNode(currentNode)) {
    if (isContainingBlock(currentNode)) {
      return currentNode;
    }
    currentNode = getParentNode(currentNode);
  }
  return null;
};
